// AutomationFilters, not the AutomationRun namespace: `parseRunsSearch` is the route's
// `validateSearch`, which stays in the entry chunk (docs/code-splitting.md).
import { AutomationFilters, type AutomationRun, type AutomationWire } from '~api';

/**
 * @desc The Runs page's URL state (spec: "List pages SHALL hold filters in the URL"). Each key is
 * absent when unset, so the default view — the overview by workflow (D9) — has a bare URL.
 *
 * - `view: 'all'` is the flat list. A filter implies it too, so an older link that carries only a
 *   filter (`?workflow=…`) still lands on the list it meant.
 * - `test: 1` includes authoring test runs in the flat list (`includeTest`); the overview never
 *   shows them.
 */
export type RunsSearch = {
  view?: 'all';
  workflow?: string;
  collector?: string;
  status?: AutomationRun.Status;
  trigger?: AutomationRun.TriggerKind;
  test?: 1;
};

export type RunsSearchPatch = { [K in keyof RunsSearch]?: RunsSearch[K] | null };

/** @desc The two views of the Runs page (D9). */
export type RunsView = 'workflow' | 'all';

const OBJECT_ID = /^[0-9a-f]{24}$/i;

const statuses = Object.values(AutomationFilters.runStatuses) as readonly string[];
const triggers = Object.values(AutomationFilters.runTriggerKinds) as readonly string[];

const idParam = (raw: unknown): string | undefined =>
  typeof raw === 'string' && OBJECT_ID.test(raw) ? raw : undefined;

/** @desc `test=1` as the router parses it (a number), or as a hand-typed string or boolean. */
const flagParam = (raw: unknown): boolean => raw === 1 || raw === '1' || raw === true;

/** @desc Lenient: an unknown or malformed value is dropped rather than refusing the page. */
export function parseRunsSearch(search: Record<string, unknown>): RunsSearch {
  const workflow = idParam(search.workflow);
  const collector = idParam(search.collector);
  const status =
    typeof search.status === 'string' && statuses.includes(search.status)
      ? (search.status as AutomationRun.Status)
      : undefined;
  const trigger =
    typeof search.trigger === 'string' && triggers.includes(search.trigger)
      ? (search.trigger as AutomationRun.TriggerKind)
      : undefined;
  return {
    ...(search.view === 'all' ? { view: 'all' as const } : {}),
    ...(workflow ? { workflow } : {}),
    ...(collector ? { collector } : {}),
    ...(status ? { status } : {}),
    ...(trigger ? { trigger } : {}),
    ...(flagParam(search.test) ? { test: 1 as const } : {}),
  };
}

/** @desc Merge a change into the search; `null` clears that key. */
export const withRunsSearch = (current: RunsSearch, patch: RunsSearchPatch): RunsSearch =>
  parseRunsSearch({ ...current, ...patch });

/** @desc Which view the search shows: the flat list when asked for or filtered, else the overview. */
export const runsViewOf = (search: RunsSearch): RunsView =>
  search.view === 'all' ||
  search.workflow !== undefined ||
  search.collector !== undefined ||
  search.status !== undefined ||
  search.trigger !== undefined ||
  search.test !== undefined
    ? 'all'
    : 'workflow';

/**
 * @desc The search patch a view switch applies: the overview clears every flat-list filter (they
 * would otherwise imply the list again); the list starts unfiltered.
 */
export const viewPatch = (view: RunsView): RunsSearchPatch =>
  view === 'all'
    ? { view: 'all' }
    : { view: null, workflow: null, collector: null, status: null, trigger: null, test: null };

/** @desc The flat list filtered to one workflow — the overview's "All runs for this workflow". */
export const workflowRunsSearch = (workflowId: string): RunsSearch => ({
  view: 'all',
  workflow: workflowId,
});

/** @desc Page size for the run list's "Load more". */
export const RUNS_PAGE_SIZE = 25;

/** @desc The `automations.run.list` input (cursor aside) the filters ask for. */
export const runsListInput = (
  search: RunsSearch,
): Omit<AutomationWire.ListRunsInput, 'cursor'> => ({
  limit: RUNS_PAGE_SIZE,
  ...(search.workflow ? { workflowId: search.workflow } : {}),
  ...(search.collector ? { collectorId: search.collector } : {}),
  ...(search.status ? { status: search.status } : {}),
  ...(search.trigger ? { trigger: search.trigger } : {}),
  ...(search.test ? { includeTest: true } : {}),
});
